import { z } from 'zod'
import { CountrySchema, SalutationSchema } from '../shopware.schemas'
import { Dictionary } from '@/localization/dictionary'
import {
  createPasswordSchema,
  houseNumberPayloadSchema,
  phoneNumberSchema,
} from '@/schemas'

export const ShortAddressSchema = z.object({
  // translated: [],
  createdAt: z.string(),

  updatedAt: z.string().nullable(),
  customerId: z.string(),
  countryId: z.string(),
  // countryStateId: null,
  salutationId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  zipcode: z.string(),
  city: z.string(),
  // company: null,
  // department: null,
  // title: null,
  street: z.string(),
  phoneNumber: z.string().nullable(),
  additionalAddressLine1: z.string().nullable(),
  additionalAddressLine2: z.string().nullable(),
  // country: null,
  // countryState: null,
  // salutation: null,
  // customFields: null,
  id: z.string(),
  // apiAlias: 'customer_address',
})

export const AddressSchema = z.object({
  // translated: [],
  createdAt: z.string(),
  // updatedAt: null,
  customerId: z.string(),
  countryId: z.string(),
  countryStateId: z.string().nullable(),
  salutationId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  zipcode: z.string(),
  city: z.string(),
  company: z.string().nullable(),
  department: z.string().nullable(),
  title: z.string().nullable(),
  street: z.string(),
  phoneNumber: z.string().nullable(),
  additionalAddressLine1: z.string().nullable(),
  additionalAddressLine2: z.string().nullable(),
  country: CountrySchema,
  // countryState: null,
  salutation: SalutationSchema,
  // customFields: null,
  id: z.string(),
  // apiAlias: 'customer_address',
})

export const AccountSchema = z.object({
  // translated: [],
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  groupId: z.string(),
  defaultPaymentMethodId: z.string().nullable(),
  salesChannelId: z.string(),
  languageId: z.string(),
  lastPaymentMethodId: z.string().nullable(),
  defaultBillingAddressId: z.string(),
  defaultShippingAddressId: z.string(),
  customerNumber: z.string(),
  salutationId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  company: z.string().nullable(),
  email: z.string().email(),
  title: z.string().nullable(),
  vatIds: z.array(z.string()).nullable(),
  affiliateCode: z.string().nullable(),
  campaignCode: z.string().nullable(),
  active: z.boolean(),
  // doubleOptInRegistration: false,
  // doubleOptInEmailSentDate: null,
  // doubleOptInConfirmDate: null,
  // hash: '',
  guest: z.boolean(),
  firstLogin: z.string().nullable(),
  accountType: z.string(),
  birthday: z.string().nullable(),
  lastOrderDate: z.string().nullable(),
  orderCount: z.number(),
  orderTotalAmount: z.number(),
  reviewCount: z.number(),
  // group: null,
  // defaultPaymentMethod: null,
  // language: null,
  // lastPaymentMethod: null,
  salutation: SalutationSchema,
  defaultBillingAddress: AddressSchema,
  defaultShippingAddress: AddressSchema,
  // activeBillingAddress: AddressSchema.nullable(),
  // activeShippingAddress: AddressSchema.nullable(),
  addresses: z.array(ShortAddressSchema),
  // tags: null,
  // tagIds: null,
  // createdById: null,
  // updatedById: null,
  // customFields: null,
  // null when double opt-in is enabled — Shopware returns no context token until the
  // customer confirms their email, so the registration response has no sw-context-token header.
  token: z.string().nullable(),
  id: z.string(),
  // apiAlias: z.string(),
  customFields: z
    .object({
      sap_id: z.string(),
    })
    .optional(),
})

export const addressPayloadSchema = z.object({
  street: z.string().min(1),
  houseNumber: houseNumberPayloadSchema,
  zipcode: z.string().min(1),
  city: z.string().min(1),
  countryId: z.string().min(1),
  phoneNumber: phoneNumberSchema,
  salutationId: z.string().min(1).optional(),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
})

const guestAddressPayloadSchema = addressPayloadSchema.extend({
  phoneNumber: phoneNumberSchema.optional(),
  salutationId: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
})

export const RegisterGuestAccountSchema = z.object({
  salutationId: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email().min(1),
  birthday: z.union([z.string(), z.instanceof(Date)]).optional(),
  birthdayDay: z.number().optional(),
  birthdayMonth: z.number().optional(),
  birthdayYear: z.number().optional(),
  password: z.string().min(8).regex(/[A-Z]/).regex(/[a-z]/),
  confirmPassword: z.string().min(8).regex(/[A-Z]/).regex(/[a-z]/),
  storefrontUrl: z.string(), //'https://default.headless1',
  guest: z.boolean(),
  languageId: z.string().min(1).optional(),
  shippingMethodId: z.string().min(1).optional(),
  billingAddress: guestAddressPayloadSchema,
  shippingAddress: guestAddressPayloadSchema.optional(),
  customFields: z
    .object({
      preferred_language: z.string().optional(),
      interests: z.string().optional(),
      customer_representative: z.string().optional(),
      customer_how_heard: z.string().optional(),
      customer_terms_conditions: z.boolean().optional(),
      customer_newsletter: z.boolean().optional(),
      data_privacy: z.boolean().optional(),
      is_customer_shop_accepted: z.boolean().optional(),
      // B2B fields
      tax_number: z.string().optional(),
      assign_new_client: z.boolean().optional(),
      assign_new_partner: z.boolean().optional(),
      ust_idnr: z.string().optional(),
      taxation_subject: z.boolean().optional(),
      former_sales_partner: z.boolean().optional(),
      b2b_applied: z.boolean().optional(),
    })
    .optional(),
})

export const getChangePasswordSchema = (
  dict?: Dictionary['account']['changePassword'],
) => {
  return z.object({
    password: z.string().min(1, dict?.validationErrors.required),
    newPassword: createPasswordSchema(dict?.validationErrors),
  })
}

export const changePasswordSchema = getChangePasswordSchema()

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
})

export const getResetPasswordFormSchema = (
  dict?: Dictionary['passwordResetForm'],
) => {
  return z.object({
    newPassword: createPasswordSchema(dict?.validationErrors),
  })
}

export const resetPasswordFormSchema = getResetPasswordFormSchema()

export const resetPasswordSchema = z
  .object({
    hash: z.string().min(1),
    newPasswordConfirm: z.string(),
  })
  .merge(resetPasswordFormSchema)

export const accountAddressDetailsSchema = z.object({
  id: z.string(),
  countryId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  zipcode: z.string(),
  city: z.string(),
  street: z.string(),
  phoneNumber: z.string(),
})

export const accountChangeProfileSchema = z.object({
  salutationId: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string(),
})

export const accountChangeEmailSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
})

export const changePhoneNumberSchema = z.object({
  countryId: z.string().min(1), // required
  firstName: z.string().min(1), // required
  lastName: z.string().min(1), // required
  city: z.string().min(1), // required
  street: z.string().min(1), // required
  phoneNumber: z.string().min(1),
  addressId: z.string(),
  zipcode: z.string().min(1), // required
})

export const createAddressSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  salutationId: z.string().optional(),
  zipcode: z.string().min(1),
  city: z.string().min(1),
  street: z.string().min(1),
  houseNumber: houseNumberPayloadSchema,
  countryId: z.string().min(1),
  phoneNumber: z.string().min(1),
  customerId: z.string().min(1),
})

export const updateAddressSchema = createAddressSchema.extend({
  addressId: z.string().min(1),
})

export const changePhoneNumberFormSchema = z.object({
  phoneNumber: z.string().min(1),
})

export const changeCustomFieldsSchema = z.object({
  assign_customers: z.boolean().optional(),
  assign_new_client: z.boolean().optional(),
  assign_new_partner: z.boolean().optional(),
  assign_recruits: z.boolean().optional(),
  tax_number: z.string().optional(),
  taxation_subject: z.boolean().optional(),
  former_sales_partner: z.boolean().optional(),
  ust_idnr: z.string().optional(),
  customer_newsletter: z.boolean().optional(),
  customer_terms_conditions: z.boolean().optional(),
  data_privacy: z.boolean().optional(),
  is_customer_shop_accepted: z.boolean().optional(),
  customer_representative: z.string().optional(),
  customer_how_heard: z.string().optional(),
  preferred_language: z.string().optional(),
  b2b_applied: z.boolean().optional(),
  interests: z.string().optional(),
  bonus_month: z.string().optional(),
  payment_option: z.string().optional(),
  bank_account_holder: z.string().optional(),
  bank_iban: z.string().optional(),
  bank_bic: z.string().optional(),
})
